import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, RefreshCw, Settings, CheckCircle, XCircle, Clock, 
  AlertCircle, Activity, PlayCircle, Zap, Link, Unlink, ExternalLink, Terminal, Shield, Loader2, DollarSign, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInCST } from '../utils/timezone';

export default function Integrations() {
  const [integrations, setIntegrations] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [health, setHealth] = useState(null);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ninjaOAuthStatus, setNinjaOAuthStatus] = useState(null);
  const [ninjaOAuthLoading, setNinjaOAuthLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    integration_type: 'ninjarmm',
    api_url: '',
    api_key: '',
    sync_interval_minutes: 60
  });

  const [warrantyKeys, setWarrantyKeys] = useState({ dell_api_key: '', lenovo_api_key: '', hp_api_key: '' });
  const [warrantyLoading, setWarrantyLoading] = useState(false);
  const [warrantySaving, setWarrantySaving] = useState(false);
  const [qbStatus, setQbStatus] = useState(null);
  const [qbSyncing, setQbSyncing] = useState(false);
  const [qbSyncResult, setQbSyncResult] = useState(null);


  const loadQbStatus = async () => {
    try { const r = await api.get('/quickbooks/status'); setQbStatus(r.data); } catch {}
  };

  const handleConnectQb = async () => {
    try {
      const currentOrigin = window.location.origin;
      const r = await api.get(`/quickbooks/authorize?from=${encodeURIComponent(currentOrigin)}`);
      if (r.data.authorization_url) window.location.href = r.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start QuickBooks authorization');
    }
  };

  const handleSyncQbPayments = async () => {
    setQbSyncing(true);
    try {
      const r = await api.post('/quickbooks/sync-payments?days_back=30');
      setQbSyncResult(r.data);
      toast.success(`Synced ${r.data.total_found} payments, ${r.data.matched} matched to invoices`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    }
    setQbSyncing(false);
  };

  const loadWarrantyKeys = async () => {
    try {
      const res = await api.get('/integrations/warranty-keys');
      if (res.data) setWarrantyKeys(res.data);
    } catch {}
  };

  const saveWarrantyKeys = async () => {
    setWarrantySaving(true);
    try {
      await api.put('/integrations/warranty-keys', warrantyKeys);
      toast.success('Warranty API keys saved');
    } catch {
      toast.error('Failed to save warranty keys');
    }
    setWarrantySaving(false);
  };

  useEffect(() => {
    loadData();
    loadWebhookStatus();
    loadNinjaOAuthStatus();
    loadWarrantyKeys();

    // Check URL params for OAuth callback result
    const params = new URLSearchParams(window.location.search);
    const ninjaAuth = params.get('ninja_auth');
    const ninjaError = params.get('ninja_error');
    const oauthResult = params.get('ninja_oauth'); // legacy
    if (ninjaAuth === 'success' || oauthResult === 'success') {
      toast.success('NinjaRMM authorized for script execution!');
      loadNinjaOAuthStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (ninjaError || oauthResult === 'error') {
      toast.error(`NinjaRMM authorization failed: ${ninjaError || params.get('msg') || 'Unknown error'}`);
      window.history.replaceState({}, '', window.location.pathname);
    }

    // QuickBooks callback
    const qbAuth = params.get('qb_auth');
    const qbError = params.get('qb_error');
    if (qbAuth === 'success') {
      toast.success('QuickBooks connected successfully!');
      loadQbStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (qbError) {
      toast.error(`QuickBooks connection failed: ${qbError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }

    loadQbStatus();
  }, []);

  const loadData = async () => {
    try {
      const [integrationsRes, historyRes, healthRes] = await Promise.allSettled([
        api.get('/integrations'),
        api.get('/sync-history?limit=20'),
        api.get('/integrations/health/overview')
      ]);
      if (integrationsRes.status === 'fulfilled') setIntegrations(integrationsRes.value.data);
      if (historyRes.status === 'fulfilled') setSyncHistory(historyRes.value.data);
      if (healthRes.status === 'fulfilled') setHealth(healthRes.value.data);
      
      const failed = [integrationsRes, historyRes, healthRes].filter(r => r.status === 'rejected');
      if (failed.length === 3) {
        toast.error('Failed to load integrations data');
      } else if (failed.length > 0) {
// removed console statement
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWebhookStatus = async () => {
    try {
      const response = await api.get('/ninjarmm/webhook/status');
      setWebhookStatus(response.data);
    } catch (error) {
      console.error('Failed to load webhook status:', error);
    }
  };

  const loadNinjaOAuthStatus = async () => {
    try {
      const response = await api.get('/ninjarmm/oauth/status');
      setNinjaOAuthStatus(response.data);
    } catch (error) {
      console.error('Failed to load NinjaRMM OAuth status:', error);
    }
  };

  const handleConnectNinjaRMM = async () => {
    setNinjaOAuthLoading(true);
    try {
      const currentOrigin = window.location.origin;
      const response = await api.get(`/ninjarmm/oauth/authorize?from=${encodeURIComponent(currentOrigin)}`);
      if (response.data.authorization_url) {
        // Redirect to NinjaRMM login for Authorization Code flow
        window.location.href = response.data.authorization_url;
      } else if (response.data.connected) {
        toast.success('NinjaRMM connected');
        loadNinjaOAuthStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start NinjaRMM authorization');
    } finally {
      setNinjaOAuthLoading(false);
    }
  };

  const handleDisconnectNinjaRMM = async () => {
    try {
      await api.post('/ninjarmm/oauth/disconnect');
      setNinjaOAuthStatus({ connected: false });
      toast.success('NinjaRMM account disconnected');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/integrations', formData);
      toast.success('Integration created successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create integration');
    }
  };

  const handleSync = async (integrationId) => {
    try {
      await api.post(`/integrations/${integrationId}/sync`);
      toast.success('Sync started');
      // Reload after a delay to show updated status
      setTimeout(loadData, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start sync');
    }
  };

  const handleTestConnection = async (integrationId) => {
    try {
      const response = await api.post(`/integrations/${integrationId}/test`);
      if (response.data.success) {
        toast.success('Connection test successful');
      }
    } catch (error) {
      toast.error('Connection test failed');
    }
  };

  const handleToggleStatus = async (integrationId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await api.put(`/integrations/${integrationId}`, { status: newStatus });
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleTestWebhook = async (eventType = 'monitoring_alert') => {
    try {
      const response = await api.post('/ninjarmm/webhook/test', {
        eventType,
        eventId: `test-${Date.now()}`,
        deviceId: '99999',
        deviceName: 'Test Device',
        condition: eventType === 'monitoring_alert' ? 'High CPU Usage (Test)' : undefined,
        severity: eventType === 'device_offline' || eventType === 'backup_failure' ? 'CRITICAL' : 'WARNING',
        timestamp: new Date().toISOString()
      });
      
      if (response.data.status === 'success') {
        toast.success(`Test webhook processed! Ticket ${response.data.ticket_id} created`);
      } else if (response.data.status === 'skipped') {
        toast.info(response.data.reason);
      }
      
      // Reload webhook status
      setTimeout(loadWebhookStatus, 1000);
    } catch (error) {
      toast.error('Failed to send test webhook');
    }
  };

  const handleSyncOrganizations = async () => {
    try {
      toast.info('Syncing NinjaRMM organizations as customers...');
      const response = await api.post('/ninjarmm/sync-organizations?auto_create_customers=true');
      
      toast.success(
        `Organizations synced! Created ${response.data.created_customers} customers, ${response.data.created_mappings} mappings`,
        { duration: 5000 }
      );
      
      // Reload data
      loadData();
    } catch (error) {
      toast.error('Failed to sync organizations');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      integration_type: 'ninjarmm',
      api_url: '',
      api_key: '',
      sync_interval_minutes: 60
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      error: 'bg-red-100 text-red-800',
      syncing: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || colors.inactive;
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: <CheckCircle className="h-4 w-4" />,
      inactive: <XCircle className="h-4 w-4" />,
      error: <AlertCircle className="h-4 w-4" />,
      syncing: <RefreshCw className="h-4 w-4 animate-spin" />
    };
    return icons[status] || icons.inactive;
  };

  const getSyncStatusColor = (status) => {
    const colors = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      partial: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || colors.in_progress;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <img 
          src="/cipherone-logo.png" 
          alt="Loading" 
          className="h-16 w-16 logo-spin mb-4"
        />
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Integrations</h1>
          <p className="text-gray-600 mt-1">Connect and sync with external systems</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-integration-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Integration
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Add New Integration</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Integration Name *</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="My NinjaRMM Instance"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="integration_type">Integration Type *</Label>
                <Select
                  value={formData.integration_type}
                  onValueChange={(value) => setFormData({ ...formData, integration_type: value })}
                >
                  <SelectTrigger data-testid="type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ninjarmm">NinjaRMM</SelectItem>
                    <SelectItem value="bitdefender">BitDefender</SelectItem>
                    <SelectItem value="unifi">Unifi</SelectItem>
                    <SelectItem value="whmcs">WHMCS</SelectItem>
                    <SelectItem value="connectwise">ConnectWise</SelectItem>
                    <SelectItem value="autotask">Autotask</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="api_url">API URL</Label>
                <Input
                  id="api_url"
                  value={formData.api_url}
                  onChange={(e) => setFormData({ ...formData, api_url: e.target.value })}
                  placeholder="https://api.example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="api_key">API Key</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  placeholder="Enter API key"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sync_interval">Sync Interval (minutes)</Label>
                <Input
                  id="sync_interval"
                  type="number"
                  value={formData.sync_interval_minutes}
                  onChange={(e) => setFormData({ ...formData, sync_interval_minutes: parseInt(e.target.value) })}
                  min={15}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-integration-button">
                  Create Integration
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Integration Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* NinjaRMM Card */}
        <Card className="border-blue-200 hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 13V6l-9-5-9 5v7c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12z"/>
              </svg>
              NinjaRMM
            </CardTitle>
            <CardDescription>Remote Monitoring & Management</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Devices:</span>
                <span className="font-semibold">269</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">API Status:</span>
                <Badge variant="custom" className="bg-green-100 text-green-800">Connected</Badge>
              </div>
              {/* Script Execution Auth Status */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Script Execution:</span>
                {ninjaOAuthStatus?.user_authorized ? (
                  <Badge variant="custom" className="bg-green-100 text-green-800 flex items-center gap-1" data-testid="ninja-oauth-connected">
                    <CheckCircle className="h-3 w-3" /> Authorized
                  </Badge>
                ) : (
                  <Badge variant="custom" className="bg-amber-100 text-amber-800 flex items-center gap-1" data-testid="ninja-oauth-disconnected">
                    <AlertCircle className="h-3 w-3" /> Requires Authorization
                  </Badge>
                )}
              </div>
              {ninjaOAuthStatus?.user_authorized && (
                <p className="text-[11px] text-gray-400">
                  User-context token active
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => window.location.href = '/ninjarmm'}>
                  Dashboard
                </Button>
                {!ninjaOAuthStatus?.user_authorized ? (
                  <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={handleConnectNinjaRMM} disabled={ninjaOAuthLoading} data-testid="connect-ninja-oauth-btn">
                    <Terminal className="h-3.5 w-3.5 mr-1" />
                    {ninjaOAuthLoading ? 'Connecting...' : 'Authorize Scripts'}
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleDisconnectNinjaRMM} data-testid="disconnect-ninja-oauth-btn">
                    <Unlink className="h-3.5 w-3.5 mr-1" /> Disconnect
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* BitDefender Card */}
        <Card className="border-purple-200 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/bitdefender'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
              </svg>
              BitDefender
            </CardTitle>
            <CardDescription>Endpoint Security & Antivirus</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Endpoints:</span>
                <span className="font-semibold">77</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Unprotected:</span>
                <span className="font-semibold text-orange-600">191</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <Badge variant="custom" className="bg-green-100 text-green-800">Connected</Badge>
              </div>
              <Button variant="outline" size="sm" className="w-full mt-2" onClick={(e) => { e.stopPropagation(); window.location.href = '/bitdefender'; }}>
                View Dashboard →
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ScreenConnect Card */}
        <Card className="border-green-200 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.location.href = '/screenconnect'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/>
              </svg>
              ScreenConnect
            </CardTitle>
            <CardDescription>Remote Access & Support</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Machines:</span>
                <span className="font-semibold">311</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Linked:</span>
                <span className="font-semibold">238</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <Badge variant="custom" className="bg-green-100 text-green-800">Connected</Badge>
              </div>
              <Button variant="outline" size="sm" className="w-full mt-2" onClick={(e) => { e.stopPropagation(); window.location.href = '/screenconnect'; }}>
                View Dashboard →
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* QuickBooks Card */}
        <Card className="hover:shadow-md transition-shadow" data-testid="quickbooks-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                QuickBooks Online
              </CardTitle>
              {qbStatus?.connected ? (
                <Badge variant="custom" className="bg-green-100 text-green-800">Connected</Badge>
              ) : (
                <Badge variant="custom" className="bg-gray-100 text-gray-600">Not Connected</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500 mb-3">Sync ACH payments from Chase Bank via QuickBooks to auto-mark invoices as paid.</p>
            {qbStatus?.connected ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Realm ID:</span>
                  <span className="font-mono text-xs">{qbStatus.realm_id}</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1" onClick={handleSyncQbPayments} disabled={qbSyncing} data-testid="sync-qb-payments-btn">
                    {qbSyncing ? 'Syncing...' : 'Sync Payments'}
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={async () => { await api.post('/quickbooks/disconnect'); setQbStatus(null); setQbSyncResult(null); toast.success('Disconnected'); }} data-testid="disconnect-qb-btn">
                    Disconnect
                  </Button>
                </div>
                {qbSyncResult && (
                  <div className="text-xs bg-green-50 border border-green-200 rounded p-2 mt-1">
                    <p><strong>{qbSyncResult.total_found}</strong> payments found, <strong>{qbSyncResult.matched}</strong> auto-matched to invoices</p>
                    {qbSyncResult.unmatched > 0 && <p className="text-amber-700">{qbSyncResult.unmatched} unmatched — assign manually in Invoices</p>}
                  </div>
                )}
              </div>
            ) : (
              <Button size="sm" className="w-full bg-green-600 hover:bg-green-700 text-white" onClick={handleConnectQb} data-testid="connect-qb-btn">
                Connect QuickBooks
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Health Overview - driven by actual /integrations/health/overview data */}
      {(() => {
        const hs = health?.health_status || 'unknown';
        const hsMeta = {
          healthy: { color: 'text-green-600', mark: '✓', label: 'All integrations fresh & error-free' },
          degraded: { color: 'text-amber-600', mark: '!', label: 'Degraded — partial/stale coverage' },
          critical: { color: 'text-red-600', mark: '×', label: 'Critical — errors detected' },
          unknown: { color: 'text-gray-500', mark: '?', label: 'Unknown — not verified' },
        }[hs] || { color: 'text-gray-500', mark: '?', label: 'Unknown — not verified' };
        const total = health?.total_integrations;
        const active = health?.active;
        const errors = health?.error;
        const fresh = health?.fresh_active ?? 0;
        const stale = health?.stale ?? 0;
        return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="integration-health-overview">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Integrations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="health-total">{total ?? '—'}</div>
            <p className="text-xs text-gray-500 mt-1">Provider inventory polling (not Ninja event delivery)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="health-active">{active ?? '—'}</div>
            <p className="text-xs text-gray-500 mt-1">{fresh} fresh · {stale} stale/unknown</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${errors ? 'text-red-600' : ''}`} data-testid="health-errors">{errors ?? '—'}</div>
            <p className="text-xs text-gray-500 mt-1">{errors == null ? 'Not verified' : errors > 0 ? 'Errors detected' : 'No errors detected'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              Health Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${hsMeta.color}`} data-testid="health-status">{hsMeta.mark}</div>
            <p className="text-xs text-gray-500 mt-1" data-testid="health-status-label">{hsMeta.label}</p>
          </CardContent>
        </Card>
      </div>
        );
      })()}

      <Tabs defaultValue="integrations" className="space-y-4">
        <TabsList>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="webhooks">NinjaRMM Webhooks</TabsTrigger>
          <TabsTrigger value="warranty">Warranty Lookup</TabsTrigger>
          <TabsTrigger value="history">Sync History</TabsTrigger>
        </TabsList>

        <TabsContent value="integrations" className="space-y-4">
          {integrations.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Zap className="h-16 w-16 text-gray-400 mb-4" />
                <p className="text-gray-600 mb-4">No integrations configured yet</p>
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Integration
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {integrations.map((integration) => (
                <Card key={integration.id} data-testid="integration-card">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{integration.name}</CardTitle>
                        <CardDescription className="mt-1">{integration.integration_type}</CardDescription>
                      </div>
                      <Badge className={getStatusColor(integration.status)}>
                        <span className="flex items-center gap-1">
                          {getStatusIcon(integration.status)}
                          {integration.status}
                        </span>
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {integration.last_sync && (
                      <div className="text-sm text-gray-600">
                        <Clock className="h-3 w-3 inline mr-1" />
                        Last sync: {formatInCST(integration.last_sync, 'MMM d, h:mm a')}
                      </div>
                    )}
                    <div className="text-sm text-gray-600">
                      Sync interval: {integration.sync_interval_minutes} minutes
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSync(integration.id)}
                        disabled={integration.status === 'syncing'}
                        data-testid="sync-button"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Sync
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTestConnection(integration.id)}
                        data-testid="test-button"
                      >
                        <PlayCircle className="h-3 w-3 mr-1" />
                        Test
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleStatus(integration.id, integration.status)}
                        data-testid="toggle-button"
                      >
                        <Settings className="h-3 w-3 mr-1" />
                        {integration.status === 'active' ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}\n            </div>
          )}
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4">
          {/* Webhook Configuration Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Webhook Configuration</CardTitle>
                  <CardDescription>Receive real-time alerts from NinjaRMM and auto-create tickets</CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleTestWebhook('monitoring_alert')}
                  data-testid="test-webhook-button"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Send Test Webhook
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {webhookStatus ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Webhook URL</Label>
                      <div className="mt-1 p-3 bg-gray-50 rounded border border-gray-200 font-mono text-xs break-all">
                        {webhookStatus.config.url}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Configure this URL in your NinjaRMM dashboard under Administration → Apps → Notification Channels
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Webhook Secret</Label>
                      <div className="mt-1 p-3 bg-gray-50 rounded border border-gray-200 font-mono text-xs">
                        {webhookStatus.config.secret_masked}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Use this secret as the encryption key in NinjaRMM webhook configuration
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">Supported Event Types</Label>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {webhookStatus.config.event_types.map((type) => (
                        <Badge key={type} variant="outline" className="font-mono text-xs">
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t">
                    <div>
                      <p className="text-sm text-gray-600">Total Webhooks</p>
                      <p className="text-2xl font-bold">{webhookStatus.statistics.total_events}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Completed</p>
                      <p className="text-2xl font-bold text-green-600">{webhookStatus.statistics.completed}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Failed</p>
                      <p className="text-2xl font-bold text-red-600">{webhookStatus.statistics.failed}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Pending</p>
                      <p className="text-2xl font-bold text-yellow-600">{webhookStatus.statistics.pending}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Success Rate</p>
                      <p className="text-2xl font-bold">{webhookStatus.statistics.success_rate}%</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">Loading webhook configuration...</div>
              )}
            </CardContent>
          </Card>

          {/* Recent Webhook Events */}
          {webhookStatus && webhookStatus.recent_events && webhookStatus.recent_events.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Recent Webhook Events</CardTitle>
                    <CardDescription>Historical webhook events (incl. prior test events) — not proof of current scoped Ninja event delivery</CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={loadWebhookStatus}
                    data-testid="refresh-webhooks-button"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {webhookStatus.recent_events.map((event) => (
                    <div 
                      key={event.id} 
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      data-testid="webhook-event-row"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className="font-mono text-xs">
                              {event.event_type}
                            </Badge>
                            <Badge className={
                              event.processing_status === 'completed' ? 'bg-green-100 text-green-800' :
                              event.processing_status === 'failed' ? 'bg-red-100 text-red-800' :
                              event.processing_status === 'processing' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }>
                              {event.processing_status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                              {event.processing_status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                              {event.processing_status === 'processing' && <Clock className="h-3 w-3 mr-1" />}
                              {event.processing_status}
                            </Badge>
                            {event.ticket_id && (
                              <Badge variant="outline" className="text-xs">
                                Ticket: {event.ticket_id.substring(0, 8)}...
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-600">
                            Event ID: <span className="font-mono">{event.event_id}</span>
                          </p>
                          {event.error_message && (
                            <p className="text-xs text-red-600">
                              Error: {event.error_message}
                            </p>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 text-right">
                          {formatInCST(event.created_at, 'MMM d, h:mm a')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Setup Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Setup Instructions</CardTitle>
              <CardDescription>How to configure webhooks in NinjaRMM dashboard</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Step 1: Access NinjaRMM Dashboard</p>
                  <p className="text-sm text-gray-600 ml-4">
                    Log into your NinjaRMM account and navigate to <span className="font-mono bg-gray-100 px-1">Administration → Apps → Notification Channels</span>
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Step 2: Create Webhook Channel</p>
                  <p className="text-sm text-gray-600 ml-4">
                    Click <span className="font-semibold">Add</span> and select <span className="font-semibold">Webhook</span> from the dropdown
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Step 3: Configure Webhook</p>
                  <ul className="text-sm text-gray-600 ml-4 space-y-1 list-disc list-inside">
                    <li>Webhook URL: Copy the URL from above</li>
                    <li>Encryption Key: Use the webhook secret shown above</li>
                    <li>Select event types: device_offline, monitoring_alert, patch_failure, backup_failure, security_threat</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Step 4: Test Configuration</p>
                  <p className="text-sm text-gray-600 ml-4">
                    Use the <span className="font-semibold">Send Test Webhook</span> button above to verify the integration is working
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="warranty" className="space-y-4">
          <Card data-testid="warranty-api-keys-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                Warranty Lookup API Keys
              </CardTitle>
              <CardDescription>
                Optional: Add manufacturer API keys for more accurate warranty lookups.
                Without API keys, the scanner uses free web scraping (less reliable but works for most devices).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="dell_api_key">Dell TechDirect API Key</Label>
                <Input
                  id="dell_api_key"
                  type="password"
                  placeholder="Enter Dell API key (from techdirect.dell.com)"
                  value={warrantyKeys.dell_api_key}
                  onChange={(e) => setWarrantyKeys(prev => ({ ...prev, dell_api_key: e.target.value }))}
                  data-testid="dell-api-key-input"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Get from <a href="https://techdirect.dell.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Dell TechDirect</a> → API Access
                </p>
              </div>
              <div>
                <Label htmlFor="lenovo_api_key">Lenovo Support API Key</Label>
                <Input
                  id="lenovo_api_key"
                  type="password"
                  placeholder="Enter Lenovo API key (optional)"
                  value={warrantyKeys.lenovo_api_key}
                  onChange={(e) => setWarrantyKeys(prev => ({ ...prev, lenovo_api_key: e.target.value }))}
                  data-testid="lenovo-api-key-input"
                />
              </div>
              <div>
                <Label htmlFor="hp_api_key">HP Developer Portal API Key</Label>
                <Input
                  id="hp_api_key"
                  type="password"
                  placeholder="Enter HP API key (optional)"
                  value={warrantyKeys.hp_api_key}
                  onChange={(e) => setWarrantyKeys(prev => ({ ...prev, hp_api_key: e.target.value }))}
                  data-testid="hp-api-key-input"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Get from <a href="https://developers.hp.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">HP Developer Portal</a>
                </p>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Button onClick={saveWarrantyKeys} disabled={warrantySaving} data-testid="save-warranty-keys-btn">
                  {warrantySaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {warrantySaving ? 'Saving...' : 'Save API Keys'}
                </Button>
                <p className="text-xs text-gray-400">Keys are encrypted at rest and only used for warranty lookups</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">How it works</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-gray-600 space-y-2">
              <p>When you scan a service tag or serial number in the Barcode Scanner:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li><strong>Local lookup</strong> — checks CipherOne assets database first</li>
                <li><strong>Web scraping</strong> — if not found, scrapes Dell/HP/Lenovo support pages for warranty info (always available)</li>
                <li><strong>API lookup</strong> — if API keys are configured above, uses manufacturer APIs for more detailed and accurate warranty data</li>
              </ol>
              <p className="text-xs text-gray-400 mt-2">API keys are optional. The free web scraping approach works for most devices but may not always extract warranty dates.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Sync History</CardTitle>
              <CardDescription>Recent synchronization activities</CardDescription>
            </CardHeader>
            <CardContent>
              {syncHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No sync history yet</p>
              ) : (
                <div className="space-y-3">
                  {syncHistory.map((sync) => (
                    <div key={sync.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{sync.integration_name}</span>
                          <Badge className={getSyncStatusColor(sync.status)}>{sync.status}</Badge>
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatInCST(sync.started_at, 'MMM d, yyyy h:mm a')}
                          {sync.duration_seconds && ` • ${sync.duration_seconds.toFixed(1)}s`}
                        </div>
                        {sync.records_synced > 0 && (
                          <div className="text-sm text-gray-600">
                            {sync.records_synced} records synced
                            {sync.records_failed > 0 && `, ${sync.records_failed} failed`}
                          </div>
                        )}
                        {sync.error_message && (
                          <div className="text-sm text-red-600 mt-1">{sync.error_message}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
